import { useCallback, useEffect, useRef, useState } from 'react';
import Padding from './padding';
import Tilt from './tilt';
import TypewriterTitle from './typewriter-title';

// `app/24/Description.js`. The copy is rendered as a numbered vim buffer: the paragraphs
// are re-wrapped by hand against a hidden probe span so every visual line can carry its
// own gutter number, and a blank numbered line separates paragraphs. Beside it, the four
// logo tiles fade in staggered and tilt together with the pointer's position on screen.
//
// The wrapping algorithm is ported as-is, measurement for measurement — its output is
// what the numbering is, so any "tidier" rewrite changes the page.

type Segment = { text: string; style?: string };
type Line = { number: number; content: Segment[] };

const LOGO_TILES = [
  'logo-tile-banana.svg',
  'logo-tile-parenthesis.svg',
  'logo-tile-slash.svg',
  'logo-tile-bracket.svg',
];

const PARAGRAPHS: Segment[][] = [
  [
    {
      text: 'platanus hack reúne el mejor talento techie de chile para crear soluciones a problemas reales.',
    },
    {
      text: ' de cero a producto en 36 horas.',
      style: 'font-bold text-primary',
    },
  ],
  [
    {
      text: 'unimos equipos de 3 a 5 personas, cafeína, mentores y pitches asistidos por platanus para tener una hackatón de nivel internacional en chile.',
    },
  ],
  [
    {
      text: 'el foco es crear soluciones a problemas reales, presentarlas como corresponde con pitches que estén a la altura de la solución y que quede deployeado, disponible a cualquier persona alrededor del mundo.',
    },
  ],
];

export default function Description() {
  const [lines, setLines] = useState<Line[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [tiltAngles, setTiltAngles] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const tilesRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const node = tilesRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const splitTextIntoLines = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const tempElement = document.createElement('span');
      tempElement.style.visibility = 'hidden';
      tempElement.style.position = 'absolute';
      tempElement.style.whiteSpace = 'normal';
      tempElement.style.font = window.getComputedStyle(
        containerRef.current,
      ).font;
      document.body.appendChild(tempElement);

      const newLines: Line[] = [];
      let lineNumber = 1;

      const processText = (paragraph: Segment[]) => {
        let currentLine = '';
        let currentLineSegments: Segment[] = [];
        let words: { word: string; style?: string }[] = [];

        for (const segment of paragraph) {
          words = words.concat(
            segment.text
              .split(' ')
              .map((word) => ({ word, style: segment.style })),
          );
        }

        for (const wordObj of words) {
          const testLine =
            currentLine + (currentLine ? ' ' : '') + wordObj.word;
          tempElement.textContent = testLine;
          const lineWidth = tempElement.offsetWidth;

          if (lineWidth > containerWidth - 50) {
            // 50px buffer for line number
            if (currentLine) {
              newLines.push({
                number: lineNumber++,
                content: currentLineSegments,
              });
            }
            currentLine = wordObj.word;
            currentLineSegments = [{ text: wordObj.word, style: wordObj.style }];
          } else {
            currentLine = testLine;
            const last = currentLineSegments[currentLineSegments.length - 1];
            if (currentLineSegments.length > 0 && last.style === wordObj.style) {
              last.text += ` ${wordObj.word}`;
            } else {
              currentLineSegments.push({
                text: wordObj.word,
                style: wordObj.style,
              });
            }
          }
        }

        if (currentLine) {
          newLines.push({ number: lineNumber++, content: currentLineSegments });
        }
      };

      PARAGRAPHS.forEach((paragraph, index) => {
        processText(paragraph);
        if (index < PARAGRAPHS.length - 1) {
          newLines.push({
            number: lineNumber++,
            content: [{ text: '' }],
          });
        }
      });

      document.body.removeChild(tempElement);
      setLines(newLines);
    };

    splitTextIntoLines();

    // The probe span measures in whatever font is active when it runs, so the line breaks
    // (and therefore the gutter numbering) depend on whether JetBrains Mono has loaded
    // yet. Next hid that race behind next/font's metric-matched "JetBrains Mono Fallback";
    // the font arrives here from Google Fonts, so measure again once it is actually in.
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled) splitTextIntoLines();
    });

    window.addEventListener('resize', splitTextIntoLines);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', splitTextIntoLines);
    };
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }

    frameRef.current = requestAnimationFrame(() => {
      if (!tilesRef.current) return;

      const rect = tilesRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const deltaX = e.clientX - centerX;
      const deltaY = e.clientY - centerY;

      const tiltY = (deltaX / window.innerWidth) * 30;
      const tiltX = -(deltaY / window.innerHeight) * 30;

      setTiltAngles({
        x: Math.max(-20, Math.min(20, tiltX)),
        y: Math.max(-20, Math.min(20, tiltY)),
      });
    });
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [handleMouseMove]);

  return (
    <section className="flex min-h-screen w-full flex-col items-center justify-center">
      <Padding>
        <div className="flex flex-col md:flex-row">
          <div className="flex flex-col md:w-1/2">
            <TypewriterTitle
              text="$ vim hack-description.txt"
              className="mb-12 text-center font-bold font-oxanium text-2xl md:text-5xl"
            />
            <div
              ref={containerRef}
              className="mx-auto mb-16 w-full max-w-2xl whitespace-pre-wrap text-left font-mono leading-relaxed"
            >
              {lines.map((line) => (
                <div key={line.number} className="flex">
                  <span className="mr-4 w-8 flex-shrink-0 text-right text-zinc-500">
                    {line.number}
                  </span>
                  <div className="flex flex-1 flex-wrap">
                    {line.content.map((segment) => (
                      <span
                        key={`${line.number}-${segment.style ?? 'plain'}-${segment.text}`}
                        className={`inline-block ${segment.style || 'text-zinc-300'}`}
                      >
                        {segment.text}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-center md:grow">
            <div
              ref={tilesRef}
              className="grid grid-cols-2 grid-rows-2 items-center justify-center gap-0"
            >
              {LOGO_TILES.map((tile, index) => (
                <Tilt
                  key={tile}
                  tiltAngleXManual={tiltAngles.x}
                  tiltAngleYManual={tiltAngles.y}
                  perspective={1000}
                  scale={1.01}
                  transitionSpeed={1000}
                  className="transition-opacity duration-300"
                >
                  <img
                    src={`/hack24/${tile}`}
                    alt={tile}
                    width={150}
                    height={150}
                    className={`m-0 box-border h-auto p-0 opacity-0 transition-opacity duration-500 ease-in-out ${isVisible ? 'opacity-100' : ''}`}
                    style={{
                      transition: `opacity 500ms ease-in-out ${index * 200}ms`,
                    }}
                  />
                </Tilt>
              ))}
            </div>
          </div>
        </div>
      </Padding>
    </section>
  );
}
